import type { RequestDoc } from '../components/Requests/requestDoc';
import { effectiveRequestStatus } from './requestRejection';

export type AdminTicketStatusFilter = 'all' | 'open' | 'in-progress' | 'closed';

export type AdminTicketSortBy = 'created' | 'last_updated';

function parseAuditTimestamp(iso: string | undefined): number {
  if (!iso?.trim()) return 0;
  const t = Date.parse(iso);
  return Number.isNaN(t) ? 0 : t;
}

/** Local calendar date (YYYY-MM-DD) for an ISO instant. */
export function localDateFromIso(iso: string | undefined): string | null {
  if (!iso?.trim()) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/**
 * Client-side filter and sort for the admin tickets table.
 * Callers should debounce text inputs and memoize results (see AdminAllTicketsPanel).
 * For datasets beyond ~200 tickets, prefer server-side pagination over raising limits.
 */
export function filterAdminTickets(options: {
  tickets: RequestDoc[];
  statusFilter: AdminTicketStatusFilter;
  createdDate: string;
  requestedBy: string;
  search: string;
  sortBy: AdminTicketSortBy;
  sortDir: 'asc' | 'desc';
}): RequestDoc[] {
  const {
    tickets,
    statusFilter,
    createdDate,
    requestedBy,
    search,
    sortBy,
    sortDir,
  } = options;

  const dateTrim = createdDate.trim();
  const personTrim = requestedBy.trim().toLowerCase();
  const searchTrim = search.trim().toLowerCase();

  const list = tickets.filter(t => {
    const effective = effectiveRequestStatus(t);
    if (statusFilter !== 'all' && effective !== statusFilter) {
      return false;
    }
    if (dateTrim) {
      const createdDay = localDateFromIso(t.audit?.created_at);
      if (createdDay !== dateTrim) {
        return false;
      }
    }
    if (personTrim) {
      const by = (t.requested_by ?? '').toLowerCase();
      if (!by.includes(personTrim)) {
        return false;
      }
    }
    if (searchTrim) {
      const hay = [
        String(t.request_id),
        t.request_name ?? '',
        t.requested_by ?? '',
      ]
        .join(' ')
        .toLowerCase();
      if (!hay.includes(searchTrim)) {
        return false;
      }
    }
    return true;
  });

  if (list.length <= 1) {
    return list;
  }

  return [...list].sort((a, b) => {
    const aT =
      sortBy === 'created'
        ? parseAuditTimestamp(a.audit?.created_at)
        : parseAuditTimestamp(a.audit?.last_updated_at);
    const bT =
      sortBy === 'created'
        ? parseAuditTimestamp(b.audit?.created_at)
        : parseAuditTimestamp(b.audit?.last_updated_at);
    const cmp = aT - bT;
    return sortDir === 'asc' ? cmp : -cmp;
  });
}
